import './Editor.css'

import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { EditorHeaderSlot } from '@editor/EditorProvider'
import PageErrorHandler from '@editor/components/PageErrorHandler'
import {
  EDITOR_REQUIRED_ANCHORS,
  EDITOR_TOOLS,
  EDITOR_TOOL_KEYS,
  EditorToolKey,
  MODAL_TYPES,
} from '@editor/config/constants'
import { CursorState } from '@editor/config/constants/cursor'
import { EditorContext, INITIAL_SHAPE_STATE } from '@editor/contexts/Editor'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { useDocumentTitle } from '@editor/hooks/useDocumentTitle'
import useInspectionGroup from '@editor/hooks/useInspectionGroup'
import { Editor as EditorInterface, MeshRefsUpdatePayload } from '@editor/interfaces/canvas'
import { EditorConfig } from '@editor/interfaces/editor'
import { FileProcessingStatus } from '@editor/interfaces/file'
import {
  AnchorPoints,
  Anchors,
  CuboidDirection,
  Cylinder,
  InspectionItem,
  MeshRefs,
  PointArray,
  Polygon,
  ShapesId,
} from '@editor/interfaces/interfaces'
import { focusCamera as focusCameraFn, focusCameraToPlane } from '@editor/services/Editor'
import { getShapes } from '@editor/services/InspectionArea'
import { patchElementOrders } from '@editor/services/InspectionGroup'
import { updateElementNames } from '@editor/services/InspectionSheet'
import { getMaskingRegions } from '@editor/services/MaskingRegion'
import { needClearAnchorFrames, withPromiseContained } from '@editor/services/Util'
import { decideActionPermission } from '@editor/services/Validation'
import {
  findCylindersWithinVolumePolygons,
  getVolumeEstimationItem,
  getVolumeEstimationItems,
} from '@editor/services/VolumeEstimation'
import { ArcballControls } from '@editor/services/threejs'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import {
  clearSuccessTools,
  clearWorkingTools,
  removeSuccessTool,
  setCursor,
  setEditedElementIds,
  setIsDragging,
  setSavingElementIds,
  setSelectedElementIds,
} from '@editor/store/tool'
import * as Sentry from '@sentry/react'
import { difference, uniq } from 'lodash'
import mixpanel from 'mixpanel-browser'
import { IoWarning } from 'react-icons/io5'
import { useLocation, useNavigate, useParams } from 'react-router'
import { Group, PerspectiveCamera, Plane, Scene, Vector3 } from 'three'
import { useDebouncedCallback } from 'use-debounce'

import AttentionText from '../common/AttentionText/AttentionText'
import { setAttentionText } from '../common/AttentionText/store/attentionText'
import { useComments } from '../common/Comments/hooks/comments'
import MissingPCDFIle from '../common/MissingPCDFIle'
import { useInspectionItems } from '../hooks/useInspectionItems'
import MainCanvas from './MainCanvas'
import DisplaySettingsManager from './components/DisplaySettingsManager'
import SettingsDrawer, { SettingsDrawerRef } from './components/SettingsDrawer'
import ToolAutoSelector from './components/ToolAutoSelector'
import { useEditor } from './hooks/editor'
import SidePanels from './infoPanels/InfoPanels'
import {
  forceOpenToolPanel,
  setHidePlaneLabel,
  setInitCameraCompleted,
  setIsBatchSaving,
  setJobRunning,
  setSelectedSubTool,
  setSelectedTool,
} from './store/editor'
import { setEditedGroupKeys } from './store/inspectionGroup'
import { setInspectionItems, setInspectionSheet, updateInspectionItems } from './store/inspectionItems'
import { setShapes, updateShapes } from './store/shapes'
import CuboidToolbar from './toolbar/CuboidToolbar'
import Toolbar from './toolbar/Toolbar'
import TransformControlsToolbar from './toolbar/TransformControls'
import ToolSubtoolbar from './toolbar/components/ToolSubtoolbar'
import { setViewerMeasurements } from './tools/CustomMeasurement/store'
import { resetWorking as resetWorkingGrid } from './tools/Grid/store'
import { setMaskRegions } from './tools/MaskPCD/store'
import { reset as resetPlaneDetection } from './tools/PlaneDetection/store'
import { selectCanChangePolylineTool } from './tools/Polyline/store/selectors'
import { reset as resetRebarDetection } from './tools/RebarDetection/store'
import { reset as resetVolumeEstimation } from './tools/VolumeEstimation/Polygon/store/volumeEstimation'
import { Tools, getCurrentEditorTool } from './tools/setup'
import { usePolylineSelector } from './tools/store/selector'

const Editor: FC = () => {
  // Router
  const location = useLocation()
  const navigate = useNavigate()
  const { project_id } = useParams<{ project_id: string }>()
  const queries = new URLSearchParams(location.search)
  const inspection_area_id = queries.get('area')

  // Hooks
  useComments()
  useEditor()
  const { fetchInspectionGroups, patchGroups } = useInspectionGroup()
  const { fetchAll, fetchInspectionItems } = useInspectionItems()

  // Store
  const dispatch = useAppDispatch()
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const areaSwitched = useAppSelector((state) => state.page.areaSwitched)
  const isPageLoading = useAppSelector((state) => state.page.isLoading)

  const userLoaded = useAppSelector((state) => state.user.userLoaded)
  const userProfile = useAppSelector((state) => state.user.userProfile)

  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const prevSelectedTool = useAppSelector((state) => state.editor.prevSelectedTool)
  const prevPrevSelectedTool = useAppSelector((state) => state.editor.prevPrevSelectedTool)
  const selectedSubTool = useAppSelector((state) => state.editor.selectedSubTool)

  const isJobRunning = useAppSelector((state) => state.editor.isJobRunning)
  const jobRunningTool = useAppSelector((state) => state.editor.jobRunningTool)
  const forceFileProcessingLoading = useAppSelector((state) => state.editor.forceFileProcessingLoading)
  const backgroundColor = useAppSelector((state) => state.editorSettings.display.general.bg_color)
  const pointSize = useAppSelector((state) => state.editorSettings.display.pcd.point_size)
  const isElementDeleting = useAppSelector((state) => state.editor.isElementDeleting)

  const cuboidAnchor = useAppSelector((state) => state.maskPCD.workingMaskCuboid)
  const selectedElementIds = useAppSelector((state) => state.toolUtil.core.selectedElementIds)

  const editedGroupKeys = useAppSelector((state) => state.inspectionGroup.editedGroupKeys)
  const editedElementIds = useAppSelector((state) => state.toolUtil.core.editedElementIds)
  const inspectionItems = useAppSelector((state) => state.inspectionItems.items)
  const shapes = useAppSelector((state) => state.shapes.shapes)

  // Permissions
  const userType = useAppSelector((state) => state.user.userType)
  const isOwner = useAppSelector((state) => state.page.isOwner)
  const isInvited = useAppSelector((state) => state.page.isInvited)
  const isAllowedToModify = decideActionPermission(isOwner, isInvited).PROJECT_DASHBOARD.MODIFY.includes(userType)
  // Write tools gate on the editor's own permission set — the product-aware one the
  // toolbar and every tool hook already use — not the page-level check above. The two
  // differ for an owner holding neither product: the page-level check is built without
  // `owned_products` and so resolves on ownership alone. It stays as-is because it also
  // drives the settings drawer and the hosts' area menu, which are not tool permissions.
  const editorPermissionSet = useAppSelector((state) => state.editor.permissionSet)
  const isAllowedToUseWriteTools = editorPermissionSet.MODIFY.includes(userType)

  // Ref to hold latest inspectionItems for use in debounced callbacks
  const inspectionItemsRef = useRef(inspectionItems)
  inspectionItemsRef.current = inspectionItems
  const isElementDeletingRef = useRef(isElementDeleting)
  isElementDeletingRef.current = isElementDeleting

  // Display settings
  const settingsDrawerRef = useRef<SettingsDrawerRef>(null)

  // Page title
  useDocumentTitle(
    `${inspectionArea?.inspection_area_name || '3D InfraLoop'}${project ? ` · ${project?.project_name}` : ''}`,
  )

  // Tool Data
  const intervals = useAppSelector((state) => state.toolGrid.intervals)
  const toolVolumePolygonIsDirty = useAppSelector((state) => state.toolVolumeEstimationPolygon.isDirty)
  const toolRebarDetectionIsDirty = useAppSelector((state) => state.toolRebarDetection.isDirty)
  const toolPlaneDetectionIsDirty = useAppSelector((state) => state.toolPlaneDetection.isDirty)
  const toolCameraProfileIsDirty = useAppSelector((state) => state.toolCameraProfile.isDirty)
  const canChangePolylineTool = usePolylineSelector(selectCanChangePolylineTool)

  const sceneRef = useRef<Scene>(null)
  const editorRef = useRef<HTMLDivElement>(null)
  const cameraRef = useRef<PerspectiveCamera | null>(null)
  const arcballControlsRef = useRef<ArcballControls | null>(null)
  const pointSelectionObjectIdsRef = useRef<string[]>([])

  //* 工事データアクセス用
  const { getAccessToken } = useContext(UserContext)
  const { showModal, showErrorModal } = useContext(GlobalModalContext)

  // Editor
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 })
  const [initCompleted, setInitCompleted] = useState(false)

  // Canvas and rendering states
  const [cuboidDirection, setCuboidDirection] = useState<CuboidDirection>()
  const [collidingShapeIds, setCollidingShapeIds] = useState<string[]>([])
  const [pointSelectionPlane, setPointSelectionPlane] = useState<Plane | null>(null)
  const [pointProjectionPlane, setPointProjectionPlane] = useState<Plane | null>(null)

  // Inspection Sheet / Items
  const [editedLayerOrders, setEditedLayerOrders] = useState<{
    inspectionItemIds: string[]
    shapeIds: ShapesId
  }>({
    inspectionItemIds: [],
    shapeIds: { polygons: [], cylinders: [] },
  })

  // Data states
  const [solidMesh, setSolidMesh] = useState<Group>()
  const [anchors, setAnchors] = useState<Anchors>(INITIAL_SHAPE_STATE())
  const [distanceAnchors, setDistanceAnchors] = useState<AnchorPoints[]>([])
  const [processingAnchor, setProcessingAnchor] = useState<PointArray>()
  const [isToolProcessing, setIsToolProcessing] = useState(false)
  const [meshRefs, setMeshRefs] = useState<MeshRefs>()
  const [selectionBoundaryBoxVectors] = useState<Vector3[][]>([])

  /**
   * Update the order of inspection items based on the new order provided.
   *
   * @param newOrder Array of objects containing inspection item IDs and their new order.
   */
  const updateElementLayerOrders = useCallback(
    (
      inspectionItemOrders: { id: string; group_id: string | null; order: number | null }[],
      cylinderOrders: { id: string; group_id: string | null; order: number | null }[],
      polygonOrders: { id: string; group_id: string | null; order: number | null }[],
    ) => {
      setEditedLayerOrders((prev) => ({
        inspectionItemIds: uniq([...prev.inspectionItemIds, ...inspectionItemOrders.map((item) => item.id)]),
        shapeIds: {
          polygons: uniq([...prev.shapeIds.polygons, ...polygonOrders.map((item) => item.id)]),
          cylinders: uniq([...prev.shapeIds.cylinders, ...cylinderOrders.map((item) => item.id)]),
        },
      }))

      dispatch(
        updateInspectionItems(
          inspectionItemOrders.map(({ id, group_id, order }) => ({
            inspection_item_id: id,
            inspection_group_id: group_id || undefined,
            order_in_inspection_group: order || undefined,
          })),
        ),
      )

      dispatch(
        updateShapes({
          cylinders: cylinderOrders.map(({ id, group_id, order }) => ({
            shape_id: id,
            inspection_group_id: group_id || undefined,
            order_in_inspection_group: order || undefined,
          })),
          polygons: polygonOrders.map(({ id, group_id, order }) => ({
            shape_id: id,
            inspection_group_id: group_id || undefined,
            order_in_inspection_group: order || undefined,
          })),
        }),
      )
    },
    [dispatch],
  )

  /**
   * Update inspection items to the server after a delay.
   */
  const updateElementOrders = useDebouncedCallback(({ inspectionItemIds, shapeIds }: typeof editedLayerOrders) => {
    if (
      (!inspectionItemIds.length && !shapeIds.cylinders.length && !shapeIds.polygons.length) ||
      !inspectionItems.length ||
      !project_id ||
      !inspection_area_id
    )
      return

    const inspectionItemsToUpdate = inspectionItems.filter((item) =>
      inspectionItemIds.includes(item.inspection_item_id!),
    )
    const polygonsToUpdate = shapes.polygons.filter((shape) => shapeIds.polygons.includes(shape.shape_id))
    const cylindersToUpdate = shapes.cylinders.filter((shape) => shapeIds.cylinders.includes(shape.shape_id))
    const itemsToUpdate: Partial<InspectionItem | Polygon | Cylinder>[] = [
      ...inspectionItemsToUpdate,
      ...polygonsToUpdate,
      ...cylindersToUpdate,
    ]
    if (!itemsToUpdate.length) return

    dispatch(setIsBatchSaving(true))

    void withPromiseContained(async () => {
      const token = await getAccessToken()
      if (!token) throw new Error('No access token available')

      const result = await patchElementOrders(token, project_id, inspection_area_id, itemsToUpdate)
      if (!result) throw new Error('Failed to update inspection items order')
    })
      .catch(async () => {
        // refetch inspection groups to reset state
        await fetchInspectionGroups()

        showErrorModal('検査グループの保存中にエラーが発生しました。\n保存前の状態にデータを戻しました。')
      })
      .finally(() => {
        dispatch(setIsBatchSaving(false))
      })
  }, 3000)

  /**
   * Effect to update element orders when they are edited.
   */
  useEffect(() => {
    updateElementOrders(editedLayerOrders)
  }, [editedLayerOrders, updateElementOrders])

  /**
   * Unified batch save for both groups and elements.
   * Triggers when either editedGroupKeys or editedElementIds changes.
   * Executes both saves in parallel for efficiency.
   */
  const debouncedBatchSave = useDebouncedCallback(
    async (groupKeys: string[], elementIds: string[]) => {
      if (!groupKeys.length && !elementIds.length) return
      if (!project) return

      // Prevent updates while deletion is in progress
      if (isElementDeletingRef.current) return

      dispatch(setIsBatchSaving(true))

      // Set saving IDs to show spinners on elements being saved
      if (elementIds.length > 0) {
        dispatch(setSavingElementIds(elementIds))
      }

      // Clear edited keys/IDs BEFORE starting saves to capture new edits during save
      if (groupKeys.length > 0) {
        dispatch(setEditedGroupKeys([]))
      }
      if (elementIds.length > 0) {
        dispatch(setEditedElementIds([]))
      }

      try {
        // Execute both saves in parallel
        const results = await Promise.allSettled([
          // Save groups if there are any edited
          groupKeys.length > 0
            ? (async () => {
                await patchGroups(groupKeys)
                return true
              })()
            : Promise.resolve(false),

          // Save elements if there are any edited
          elementIds.length > 0
            ? (async () => {
                await updateElementNames(
                  getAccessToken,
                  project.project_id,
                  elementIds,
                  inspectionItemsRef.current, // Use ref to get latest value
                  showErrorModal,
                  async () => {
                    await fetchInspectionItems()
                  },
                )
                return true
              })()
            : Promise.resolve(false),
        ])

        const [groupsResult, elementsResult] = results

        // Silently handle any failures - error handling is done within each save function
        if (groupsResult.status === 'rejected' || elementsResult.status === 'rejected') {
          // Errors are already handled by individual save functions
        }
      } catch (error) {
        // Capture unexpected errors to Sentry
        Sentry.captureException(error)
      } finally {
        dispatch(setSavingElementIds([]))

        dispatch(setIsBatchSaving(false))
      }
    },
    3000, // 3 second debounce
    {
      leading: false,
      trailing: true,
    },
  )

  /**
   * Trigger unified batch save when either groups or elements change.
   */
  useEffect(() => {
    if (editedGroupKeys.length > 0 || editedElementIds.length > 0) {
      void debouncedBatchSave(editedGroupKeys, editedElementIds)
    }
  }, [editedGroupKeys, editedElementIds, debouncedBatchSave])

  /**
   * Given 2 planes, find all cylinders that are within the volume of the 2 planes
   * @param planes Array of plane shape ids
   */
  const findCylindersWithinVolumePlanes = useCallback(
    (planes: string[]): string[] => {
      const polygons = shapes.polygons.filter((shape) => planes.includes(shape.shape_id))
      const cylinders = findCylindersWithinVolumePolygons(polygons as [Polygon, Polygon], shapes.cylinders)
      return cylinders.map((cylinder) => cylinder.shape_id)
    },
    [shapes],
  )

  /**
   * Add or remove additional shape ids when selecting a shape
   * TODO: Figure out how to do in modules.
   * @param shapeIds All shapeIDs to be selected including current ones
   */
  const addAdditionalSelectedShapeIds = useCallback(
    (shapeIds: string[], newTool?: string): string[] => {
      let newCollection = [...shapeIds]
      const volumeItems = getVolumeEstimationItems(inspectionItems)
      const currentTool = getCurrentEditorTool(newTool || selectedTool)

      if (volumeItems.length) {
        // If tool is configured only for single selectable volume,
        // when there's more than 1 volume selected, deselect all except for one.
        if (currentTool?.config?.volume?.onlyOneSelectable) {
          const selectedVolumesByPlanes = newCollection.reduce<Record<string, number>>((collection, id) => {
            const selectedVolume = volumeItems.find((item) => (item.shape_ids.polygons || []).includes(id))
            if (selectedVolume) {
              if (collection[selectedVolume.inspection_item_id!]) {
                collection[selectedVolume.inspection_item_id!] += 1
              } else {
                collection[selectedVolume.inspection_item_id!] = 1
              }
            }

            return collection
          }, {})

          // Filter shape ids to last selected volume
          if (Object.keys(selectedVolumesByPlanes).length > 1) {
            const selectedVolumeIds = Object.keys(selectedVolumesByPlanes)
            const lastSelectedVolumeId = selectedVolumeIds[selectedVolumeIds.length - 1]
            newCollection = newCollection.filter((id) => {
              const selectedVolume = volumeItems.find((item) => (item.shape_ids.polygons || []).includes(id))
              return selectedVolume?.inspection_item_id === lastSelectedVolumeId
            })
          }
        }

        // ## Add both planes of volume estimation planes if one is selected
        const missingPlanes = volumeItems
          .map((item) => (item.shape_ids.polygons || [])?.filter((value) => !shapeIds.includes(value)))
          .filter((arr) => arr.length === 1)
          .flat()
        const toRemove = !!missingPlanes.filter((value) => selectedElementIds.includes(value)).length

        if (missingPlanes.length === 1) {
          // add all missing plane ids to the collection
          newCollection = newCollection.concat(missingPlanes)

          // user is un-selecting a volume plane
          if (toRemove) {
            const toRemoveIds = volumeItems
              .filter((item) => (item.shape_ids.polygons || [])?.some((value) => missingPlanes.includes(value)))
              .map((item) => item.shape_ids.polygons || [])
              .flat()
            newCollection = newCollection.filter((value) => !toRemoveIds.includes(value))
          }
        }

        // If we're on depth estimation, add or remove cylinders located within the selected volume
        if (selectedTool === EDITOR_TOOLS.DEPTH || newTool === EDITOR_TOOLS.DEPTH) {
          // find out if all new selected shapes are volume planes
          const newShapes = difference(newCollection, selectedElementIds)
          const areAllNewVolumePlanes =
            newShapes.length > 0 &&
            newShapes.every((id) => volumeItems.some((item) => (item.shape_ids.polygons || []).includes(id)))

          // find out if all deselected shapes are volume planes
          const removedShapes = difference(selectedElementIds, newCollection)
          const areAllRemovedVolumePlanes =
            removedShapes.length > 0 &&
            removedShapes.every((id) => volumeItems.some((item) => (item.shape_ids.polygons || []).includes(id)))

          // we need to group it by volume so we don't accidentally merge planes of different volume.
          if (areAllRemovedVolumePlanes) {
            const deselectedVolumes = volumeItems
              .filter((item) => (item.shape_ids.polygons || [])?.some((value) => removedShapes.includes(value)))
              .map((item) => item.shape_ids.polygons || [])
            const deselectedCylinders = deselectedVolumes.map((vol) => findCylindersWithinVolumePlanes(vol))
            newCollection = newCollection.filter((id) => !deselectedCylinders.flat().includes(id))
          }

          // If we're on depth estimation, add all cylinders located within the selected plane
          // this only applies when changing tool or selecting a plane
          if (areAllNewVolumePlanes) {
            // we need to group it by volume so we don't accidentally merge planes of different volume.
            const selectedVolumes = volumeItems
              .filter((item) => (item.shape_ids.polygons || [])?.some((value) => newShapes.includes(value)))
              .map((item) => item.shape_ids.polygons || [])

            newCollection = uniq(
              newCollection.concat(selectedVolumes.map((vol) => findCylindersWithinVolumePlanes(vol)).flat()),
            )
          }
        }
      }

      return newCollection
    },
    [inspectionItems, selectedElementIds, selectedTool, findCylindersWithinVolumePlanes],
  )

  /**
   * Set shape IDs, optionally adding any related shapes as well.
   */
  const changeSelectedShapeIds = useCallback(
    (shapeIds: string[]) => {
      dispatch(setSelectedElementIds(addAdditionalSelectedShapeIds(shapeIds)))
    },
    [addAdditionalSelectedShapeIds, dispatch],
  )

  /**
   * Filters or add shapes based on currently selected shapes.
   */
  const filterOrAddSelectedShapeIds = useCallback(
    (newTool?: string) => {
      dispatch(setSelectedElementIds(addAdditionalSelectedShapeIds(selectedElementIds, newTool)))
    },
    [selectedElementIds, addAdditionalSelectedShapeIds, dispatch],
  )

  //* Clean the anchor picking status
  //* Should be called before changing tool
  const resetState = useCallback(() => {
    const shapeKey = EDITOR_TOOL_KEYS[selectedTool]

    if (shapeKey) {
      const requiredAnchors = EDITOR_REQUIRED_ANCHORS[shapeKey]
      const anchorPoints = [...anchors[shapeKey]]
      const lastAnchor = anchorPoints.pop()

      if (processingAnchor || (lastAnchor?.points?.length && lastAnchor.points.length < requiredAnchors)) {
        setProcessingAnchor(undefined)
        setAnchors({ ...anchors, [shapeKey]: anchorPoints })
      }
    } else if (selectedTool === EDITOR_TOOLS.DISTANCE) {
      const requiredAnchors = 2
      const anchorPoints = [...distanceAnchors]
      const lastAnchor = anchorPoints.pop()

      if (processingAnchor || (lastAnchor?.points?.length && lastAnchor.points.length < requiredAnchors)) {
        setProcessingAnchor(undefined)
        setDistanceAnchors(anchorPoints)
      }
    }

    setIsToolProcessing(false)
    dispatch(setIsDragging(false))
    dispatch(setAttentionText({ message: '' }))
  }, [selectedTool, anchors, processingAnchor, distanceAnchors, dispatch])

  /**
   * Check if the tool is the previous tool, or the tool before the previous tool
   * if the previous tool is FOCUS.
   */
  const isPreviousTool = useCallback(
    (tool: string) =>
      prevSelectedTool === tool || (prevSelectedTool === EDITOR_TOOLS.FOCUS && prevPrevSelectedTool === tool),
    [prevSelectedTool, prevPrevSelectedTool],
  )

  /**
   * Finalize changing the tool by updating relevant states and dispatching actions.
   */
  const finalizeChangingTool = useCallback(
    (tool: string) => {
      // TODO: Move to tool module
      if (tool === EDITOR_TOOLS.COMMENT) {
        dispatch(setCursor(CursorState.CROSSHAIR))
      } else {
        dispatch(setCursor(CursorState.NONE))
      }

      if (tool !== EDITOR_TOOLS.MOVE) {
        dispatch(forceOpenToolPanel())
      }

      dispatch(removeSuccessTool(tool))
      dispatch(setSelectedTool(tool))

      const currentTool = getCurrentEditorTool(tool)
      if (currentTool?.config?.volume?.onlyOneSelectable) {
        filterOrAddSelectedShapeIds(tool)
      }

      mixpanel.track('Change tool', {
        'Tool (new)': tool,
        'Tool (old)': selectedTool,
      })
    },
    [selectedTool, filterOrAddSelectedShapeIds, dispatch],
  )

  /**
   * Reset all tools and states
   */
  const resetTools = useCallback(() => {
    setAnchors(INITIAL_SHAPE_STATE())
    dispatch(setHidePlaneLabel(false))

    // TODO: Some of this is already done in their respective useEditor hooks.
    //       Move the rest of the logic there and remove this.
    dispatch(resetWorkingGrid())
    dispatch(resetVolumeEstimation())
    dispatch(resetRebarDetection())
    dispatch(resetPlaneDetection())
  }, [dispatch])

  /**
   * Change the current tool, handling any necessary checks and state resets.
   */
  const changeTool = useCallback(
    (tool: string, skipClearAnchorFramesChecking?: boolean) => {
      resetState()

      const deps = getCurrentEditorTool(jobRunningTool)?.config?.dependencies
      const isDependentTool = deps?.includes(tool) || false

      if (isJobRunning && isDependentTool) {
        showErrorModal('データを更新中です。完了後に再度実行してください。')
        return
      }

      // need volume to use tool
      const volumeInspectionItem = getVolumeEstimationItem(inspectionItems)
      const toolRequireVolume = Tools.find((t) => t.key === tool)?.config?.volume?.required || false
      if (toolRequireVolume && !volumeInspectionItem) {
        showErrorModal('体積測定を先に実施してください。')
        return
      }

      // need cylinder to use tool
      const hasCylinders = shapes.cylinders?.length > 0
      const toolRequireCylinder = Tools.find((t) => t.key === tool)?.config?.cylinder?.required || false
      if (toolRequireCylinder && !hasCylinders) {
        showErrorModal('先に鉄筋を検出してください。')
        return
      }

      // clear guidelines if any
      if (
        !isJobRunning &&
        !skipClearAnchorFramesChecking &&
        needClearAnchorFrames(
          tool,
          cuboidAnchor,
          intervals,
          toolVolumePolygonIsDirty,
          toolRebarDetectionIsDirty,
          toolPlaneDetectionIsDirty,
          !canChangePolylineTool && selectedTool === EDITOR_TOOLS.POLYLINE,
          toolCameraProfileIsDirty,
        )
      ) {
        showModal({
          modalType: MODAL_TYPES.CONFIRMATION_CRITICAL,
          body: '検出を破棄しますか？',
          confirmText: '破棄',
          cancelText: 'キャンセル',
          onConfirm: () => {
            document.dispatchEvent(
              new CustomEvent<{ newTool: string | undefined; prevSelectedTool: string | undefined }>(
                'editor:tool:changeReset',
                {
                  // Previous tool is essentially the current tool as we're changing away from it
                  detail: { newTool: tool, prevSelectedTool: selectedTool },
                },
              ),
            )

            resetTools()
            finalizeChangingTool(tool)

            mixpanel.track('Reset tool', {
              Tool: tool,
              Reason: 'Change tool',
            })
            return true
          },
        })
      } else {
        finalizeChangingTool(tool)

        document.dispatchEvent(
          new CustomEvent<{ newTool: string | undefined; prevSelectedTool: string | undefined }>('editor:tool:change', {
            // Previous tool is essentially the current tool as we're changing away from it
            detail: { newTool: tool, prevSelectedTool: selectedTool },
          }),
        )
      }
    },
    [
      selectedTool,
      cuboidAnchor,
      inspectionItems,
      isJobRunning,
      jobRunningTool,
      intervals,
      shapes,
      toolRebarDetectionIsDirty,
      toolVolumePolygonIsDirty,
      toolPlaneDetectionIsDirty,
      canChangePolylineTool,
      toolCameraProfileIsDirty,
      resetTools,
      finalizeChangingTool,
      resetState,
      showErrorModal,
      showModal,
    ],
  )

  /**
   * Focus the camera on the anchor point
   */
  const focusCamera = useCallback(
    (anchorPoint: PointArray | undefined) => {
      if (!anchorPoint || !cameraRef.current || !arcballControlsRef.current) return

      focusCameraFn(arcballControlsRef.current, cameraRef.current, new Vector3(...anchorPoint), Infinity)
    },
    [cameraRef, arcballControlsRef],
  )

  /**
   * Focus the camera on the anchor point based on a specified normal vector.
   *
   * @param point The point to focus the camera on
   * @param normal The normal vector to point the camera towards
   * @param right The right vector of the camera
   * @param distance The distance from the target point
   */
  const focusCameraDirectional = useCallback(
    (point: PointArray, normal: Vector3, right: Vector3, distance?: number) => {
      if (!point || !cameraRef.current || !arcballControlsRef.current) return

      focusCameraToPlane(arcballControlsRef.current, cameraRef.current, point, normal, right, undefined, distance)
    },
    [cameraRef, arcballControlsRef],
  )

  const changeSubTool = useCallback(
    (subTool: string) => {
      dispatch(setSelectedSubTool(subTool))
    },
    [dispatch],
  )

  const changeCuboidDirection = useCallback((direction: CuboidDirection) => {
    setCuboidDirection(direction)
  }, [])
  const changeIsJobRunning = useCallback(
    (running: boolean) => {
      dispatch(setJobRunning({ running, tool: selectedTool }))
    },
    [selectedTool, dispatch],
  )
  const changeIsToolProcessing = useCallback((processing: boolean) => {
    setIsToolProcessing(processing)
  }, [])
  const updateMeshRefs = useCallback((ref: MeshRefsUpdatePayload) => {
    setMeshRefs((refs) => ({
      // remove IDs explicitly marked with null for deletion
      ...Object.entries(refs || {}).reduce((acc, [key, value]) => {
        if (value && ref[key] !== null) acc[key] = value
        return acc
      }, {} as MeshRefs),

      // we don't need to keep the null values
      ...Object.entries(ref).reduce((acc, [key, value]) => {
        if (value) acc[key] = value
        return acc
      }, {} as MeshRefs),
    }))
  }, [])
  const changeCollidingShapeIds = useCallback((shapeIds: string[]) => {
    setCollidingShapeIds(shapeIds)
  }, [])

  const contextValue: EditorInterface = useMemo(
    () => ({
      sceneRef,
      backgroundColor,
      cuboidDirection,
      changeCuboidDirection,
      changeIsJobRunning,
      changeIsToolProcessing,
      changeSelectedShapeIds,
      changeTool,
      changeSubTool,
      initCompleted,
      isToolProcessing,
      meshRefs,
      pointSize,
      selectedTool,
      selectedSubTool,
      prevSelectedTool,
      prevPrevSelectedTool,
      isPreviousTool,
      updateMeshRefs,
      updateElementLayerOrders,
      changeCollidingShapeIds,
      collidingShapeIds,
      selectionBoundaryBoxVectors,
      cameraRef,
      arcballControlsRef,
      focusCamera,
      focusCameraDirectional,

      solidMesh,
      setSolidMesh,

      pointSelectionPlane,
      setPointSelectionPlane,

      pointProjectionPlane,
      setPointProjectionPlane,

      pointSelectionObjectIdsRef,
    }),
    [
      sceneRef,

      backgroundColor,
      cuboidDirection,
      changeCuboidDirection,
      changeIsJobRunning,
      changeIsToolProcessing,
      changeSelectedShapeIds,
      changeTool,
      changeSubTool,
      initCompleted,
      isToolProcessing,
      meshRefs,
      pointSize,
      selectedTool,
      selectedSubTool,
      prevSelectedTool,
      prevPrevSelectedTool,
      isPreviousTool,
      updateMeshRefs,
      updateElementLayerOrders,
      changeCollidingShapeIds,
      collidingShapeIds,
      selectionBoundaryBoxVectors,
      cameraRef,
      arcballControlsRef,
      focusCamera,
      focusCameraDirectional,

      solidMesh,
      setSolidMesh,

      pointSelectionPlane,
      setPointSelectionPlane,

      pointProjectionPlane,
      setPointProjectionPlane,

      pointSelectionObjectIdsRef,
    ],
  )

  const handleResize = useCallback(() => {
    const canvasBox = document.getElementById('canvasBox') as HTMLElement
    if (!canvasBox) return

    setWindowSize({
      width: window.innerWidth,
      height: canvasBox.clientHeight,
    })
  }, [])

  /**
   * Mixpanel
   */
  // Super properties to register
  const mixpanelSuperPropsRef = useRef({
    Page: 'Editor',
    'Project ID': project_id,
    'Inspection Area ID': inspection_area_id,
    'Original File Name': inspectionArea?.origin_file?.name,
  })

  // Cleanup super properties
  useEffect(() => () => Object.keys(mixpanelSuperPropsRef.current).forEach((prop) => mixpanel.unregister(prop)), [])

  /**
   * If area has already been set and inspection_area_id is different, reset store/states
   */
  useEffect(() => {
    if (areaSwitched?.to?.inspection_area_id) {
      dispatch(setShapes(INITIAL_SHAPE_STATE()))
      dispatch(setInspectionSheet(undefined))
      dispatch(setInspectionItems([]))
      dispatch(setSelectedElementIds([]))
      dispatch(setMaskRegions([]))
      setInitCompleted(false)
      resetTools()
      dispatch(setSelectedTool(EDITOR_TOOLS.MOVE))
      dispatch(setCursor(CursorState.NONE))
      dispatch(setViewerMeasurements([]))
      dispatch(clearWorkingTools())
      dispatch(clearSuccessTools())
      dispatch(setInitCameraCompleted(false))
    }
  }, [areaSwitched?.to?.inspection_area_id, resetTools, dispatch])

  /**
   * Load everything needed for Editor page.
   */
  useEffect(() => {
    void (async () => {
      if (
        !initCompleted &&
        project?.project_id &&
        userLoaded &&
        project_id &&
        inspection_area_id &&
        inspectionArea?.inspection_area_id === inspection_area_id
      ) {
        const token = await getAccessToken()
        if (!token) {
          setInitCompleted(true)
          return
        }

        const allData = await Promise.all([
          getShapes(token, project_id, inspection_area_id, showErrorModal),
          fetchAll(),
          getMaskingRegions(token, project_id, inspection_area_id, showErrorModal),
          fetchInspectionGroups(),
        ])

        if (allData[0]) {
          dispatch(setShapes(allData[0]))
        } else {
          dispatch(setShapes(INITIAL_SHAPE_STATE()))
        }

        if (allData[2]) {
          dispatch(setMaskRegions(allData[2]))
        }

        setInitCompleted(true)

        // analytics
        mixpanel.register(mixpanelSuperPropsRef.current)
        mixpanel.track('Page View')
      }
    })()
  }, [
    initCompleted,
    userLoaded,
    project?.project_id,
    project_id,
    inspectionArea?.inspection_area_id,
    inspection_area_id,
    getAccessToken,
    showErrorModal,
    dispatch,
    fetchAll,
    fetchInspectionGroups,
  ])

  /**
   * Prevent iPad from pulling to refresh
   * canvasがリサイズされるたびに再計算
   */
  useEffect(() => {
    window.addEventListener('resize', handleResize)
    handleResize()
    document.body.classList.add('no-overflow')

    return () => {
      window.removeEventListener('resize', handleResize)
      document.body.classList.remove('no-overflow')
    }
  }, [handleResize])

  /**
   * When a job is completed, switch back to Move tool if the completed job's tool is currently selected.
   */
  useEffect(() => {
    const handleJobCompleted = ((ev: CustomEvent<{ tool: EditorToolKey }>) => {
      const { tool } = ev.detail
      if (tool === selectedTool) {
        changeTool(EDITOR_TOOLS.MOVE, true)
      }
    }) as (ev: Event) => void

    document.addEventListener('editor:job-completed', handleJobCompleted)

    return () => {
      document.removeEventListener('editor:job-completed', handleJobCompleted)
    }
  }, [selectedTool, changeTool])

  /**
   * Hacky way to use router from within ThreeJS
   */
  useEffect(() => {
    const goto = (ev: Event) => {
      const {
        detail: { url, replace },
      } = ev as CustomEvent<{ url: string; replace: boolean }>
      void navigate(url, { replace })
    }

    document.addEventListener('app:navigate', goto)

    return () => {
      document.removeEventListener('app:navigate', goto)
    }
  }, [navigate])

  /**
   * Listen to actions triggered on navbar.
   */
  useEffect(() => {
    const handleEditSettings = () => {
      settingsDrawerRef.current?.openDrawer()
    }

    window.addEventListener('editor:edit-settings', handleEditSettings)

    return () => {
      window.removeEventListener('editor:edit-settings', handleEditSettings)
    }
  }, [settingsDrawerRef])

  // Tools activation by running hook
  // Regardless if user can't use it, the hook must run.
  const toolsConfig = Tools.map((tool) => {
    const tools: (
      | {
          key: string
          auth: boolean
          hook: EditorConfig
        }
      | undefined
    )[] = []

    if (tool.hooks?.useEditor) {
      tools.push({
        key: tool.key,
        auth: tool.authCheck(userProfile, isAllowedToUseWriteTools),
        hook: tool.hooks?.useEditor(contextValue),
      })
    }

    if (tool.toolbar?.variants) {
      tools.push(
        ...tool.toolbar.variants.map(
          (variant) =>
            variant.hooks?.useEditor && {
              key: variant.key,
              auth: variant.authCheck(userProfile, isAllowedToUseWriteTools),
              hook: variant.hooks?.useEditor(contextValue),
            },
        ),
      )
    }

    return tools
  })
    .filter(Boolean)
    .flat()
  const toolsComponents = Tools.map((tool) => tool.components?.editor)
    .flat()
    .filter((component) => !!component) as FC[]

  // No inspection area
  if (!isPageLoading && !inspectionArea?.inspection_area_id) {
    return (
      <PageErrorHandler>
        <VStack p={0} m={0} h="100svh" gap={0}>
          <EditorHeaderSlot />
          <Flex
            bg="black"
            flex={1}
            w="100%"
            color="white"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            gap={3}
          >
            <IoWarning size="7rem" color="yellow" />
            <Text fontSize="lg" color="gray2.300">
              エリアが見つかりませんでした。
            </Text>
          </Flex>
        </VStack>
      </PageErrorHandler>
    )
  }

  // PCD file is not uploaded yet
  if (
    inspectionArea &&
    (!inspectionArea.down_sampled_file?.name ||
      inspectionArea.downsample_status?.status !== FileProcessingStatus.SUCCEEDED)
  ) {
    return <MissingPCDFIle forceFileProcessingLoading={forceFileProcessingLoading} />
  }

  return (
    <PageErrorHandler>
      <EditorContext.Provider value={contextValue}>
        <ToolAutoSelector />
        <DisplaySettingsManager />

        <VStack p={0} m={0} h="100svh" gap={0}>
          <EditorHeaderSlot />
          <Box
            w="100svw"
            h="100%"
            className="page editor"
            ref={editorRef}
            position="relative"
            flex="1"
            marginTop="0 !important"
          >
            <Flex justify="space-between" align="start" h="100%">
              <Box id="canvasBox" flex={1} h="100%">
                <MainCanvas windowSize={windowSize} />
              </Box>

              {initCompleted && (
                <>
                  <Toolbar />

                  {!isJobRunning &&
                    Tools.find((tool) => selectedTool === tool.key)?.config?.additionalToolbars?.cuboidControls &&
                    cuboidAnchor?.points.length === EDITOR_REQUIRED_ANCHORS.cuboid && <CuboidToolbar />}

                  <ToolSubtoolbar toolsConfig={toolsConfig} />

                  <TransformControlsToolbar />

                  <SidePanels toolsConfig={toolsConfig} />
                </>
              )}
            </Flex>
          </Box>

          {initCompleted ? (
            <>
              <AttentionText />

              {toolsComponents.map((ToolComponent) => (
                <ToolComponent key={`editor-tool-component-${ToolComponent.name}`} />
              ))}
            </>
          ) : null}
        </VStack>
      </EditorContext.Provider>
      {/* <DebugPanel /> */}
      {isAllowedToModify && <SettingsDrawer ref={settingsDrawerRef} />}
    </PageErrorHandler>
  )
}

export default Editor
